import { useReducer, useCallback, useEffect, useRef } from 'react';
import { useAuth } from '@/state/auth-context';
import { deleteConversation } from '@/lib/api';
import type { ChatMessage, ToolCallInfo, SSEEvent, MessageSegment } from '@/types/log-debugger';
import { config } from '@/config/env';

const API_BASE_URL = config.api.adminBaseUrl;

interface LogDebuggerState {
  messages: ChatMessage[];
  isStreaming: boolean;
  activeToolCall: ToolCallInfo | null;
  conversationId: string | null;
  error: string | null;
  tokensUsed: number;
}

type Action =
  | { type: 'ADD_USER_MESSAGE'; message: ChatMessage }
  | { type: 'ADD_ASSISTANT_MESSAGE'; id: string }
  | { type: 'APPEND_TEXT_DELTA'; content: string }
  | { type: 'SET_TOOL_START'; toolCall: ToolCallInfo }
  | { type: 'SET_TOOL_RESULT'; toolUseId: string; summary: string }
  | { type: 'SET_ERROR'; error: string }
  | { type: 'SET_DONE'; conversationId: string; tokensUsed: number }
  | { type: 'SET_STREAMING'; isStreaming: boolean }
  | { type: 'CLEAR_CONVERSATION' };

const SESSION_STORAGE_KEY = `log-debugger-session:${config.api.adminBaseUrl}`;

function loadPersistedState(): Partial<LogDebuggerState> {
  try {
    const stored = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (!stored) return {};
    const parsed = JSON.parse(stored) as { conversationId: string; messages: ChatMessage[] };
    return { conversationId: parsed.conversationId, messages: parsed.messages };
  } catch {
    return {};
  }
}

function reducer(state: LogDebuggerState, action: Action): LogDebuggerState {
  switch (action.type) {
    case 'ADD_USER_MESSAGE':
      return { ...state, messages: [...state.messages, action.message], error: null };

    case 'ADD_ASSISTANT_MESSAGE':
      return {
        ...state,
        messages: [
          ...state.messages,
          {
            id: action.id,
            role: 'assistant',
            content: '',
            timestamp: new Date().toISOString(),
            toolCalls: [],
            segments: [],
          },
        ],
      };

    case 'APPEND_TEXT_DELTA': {
      const messages = [...state.messages];
      const last = messages[messages.length - 1];
      if (last && last.role === 'assistant') {
        const segments: MessageSegment[] = [...(last.segments || [])];
        const lastSegment = segments[segments.length - 1];
        if (lastSegment && lastSegment.type === 'text') {
          segments[segments.length - 1] = {
            type: 'text',
            content: lastSegment.content + action.content,
          };
        } else {
          segments.push({ type: 'text', content: action.content });
        }
        messages[messages.length - 1] = {
          ...last,
          content: last.content + action.content,
          segments,
        };
      }
      return { ...state, messages };
    }

    case 'SET_TOOL_START': {
      const messages = [...state.messages];
      const last = messages[messages.length - 1];
      if (last && last.role === 'assistant') {
        const segments: MessageSegment[] = [...(last.segments || [])];
        segments.push({ type: 'tool_call', toolCall: action.toolCall });
        messages[messages.length - 1] = {
          ...last,
          toolCalls: [...(last.toolCalls || []), action.toolCall],
          segments,
        };
      }
      return { ...state, messages, activeToolCall: action.toolCall };
    }

    case 'SET_TOOL_RESULT': {
      const messages = [...state.messages];
      const last = messages[messages.length - 1];
      if (last && last.role === 'assistant') {
        const toolCalls = (last.toolCalls || []).map(tc =>
          tc.toolUseId === action.toolUseId
            ? { ...tc, resultSummary: action.summary, isActive: false }
            : tc
        );
        const segments: MessageSegment[] = (last.segments || []).map(seg =>
          seg.type === 'tool_call' && seg.toolCall.toolUseId === action.toolUseId
            ? {
                type: 'tool_call',
                toolCall: { ...seg.toolCall, resultSummary: action.summary, isActive: false },
              }
            : seg
        );
        messages[messages.length - 1] = { ...last, toolCalls, segments };
      }
      return { ...state, messages, activeToolCall: null };
    }

    case 'SET_ERROR':
      return { ...state, error: action.error, isStreaming: false };

    case 'SET_DONE': {
      const newState = {
        ...state,
        conversationId: action.conversationId,
        tokensUsed: state.tokensUsed + action.tokensUsed,
        isStreaming: false,
      };
      try {
        sessionStorage.setItem(
          SESSION_STORAGE_KEY,
          JSON.stringify({ conversationId: newState.conversationId, messages: newState.messages })
        );
      } catch {
        // Silently ignore storage errors
      }
      return newState;
    }

    case 'SET_STREAMING':
      return { ...state, isStreaming: action.isStreaming };

    case 'CLEAR_CONVERSATION':
      try {
        sessionStorage.removeItem(SESSION_STORAGE_KEY);
      } catch {
        // Silently ignore storage errors
      }
      return {
        messages: [],
        isStreaming: false,
        activeToolCall: null,
        conversationId: null,
        error: null,
        tokensUsed: 0,
      };

    default:
      return state;
  }
}

export interface UseLogDebuggerReturn {
  messages: ChatMessage[];
  isStreaming: boolean;
  activeToolCall: ToolCallInfo | null;
  conversationId: string | null;
  error: string | null;
  tokensUsed: number;
  sendMessage: (
    message: string,
    opts?: { logGroups?: string[]; timeRange?: { start: string; end: string } }
  ) => void;
  clearConversation: () => void;
  stopStreaming: () => void;
}

export function useLogDebugger(): UseLogDebuggerReturn {
  const [state, dispatch] = useReducer(reducer, null, () => {
    const persisted = loadPersistedState();
    return {
      messages: persisted.messages || [],
      isStreaming: false,
      activeToolCall: null,
      conversationId: persisted.conversationId || null,
      error: null,
      tokensUsed: 0,
    };
  });
  const { getIdToken } = useAuth();
  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => () => abortControllerRef.current?.abort(), []);

  const sendMessage = useCallback(
    async (
      message: string,
      opts?: { logGroups?: string[]; timeRange?: { start: string; end: string } }
    ) => {
      const token = getIdToken();
      if (!token) return;

      dispatch({
        type: 'ADD_USER_MESSAGE',
        message: {
          id: crypto.randomUUID(),
          role: 'user',
          content: message,
          timestamp: new Date().toISOString(),
        },
      });

      dispatch({ type: 'ADD_ASSISTANT_MESSAGE', id: crypto.randomUUID() });
      dispatch({ type: 'SET_STREAMING', isStreaming: true });

      const controller = new AbortController();
      abortControllerRef.current = controller;

      try {
        const response = await fetch(`${API_BASE_URL}/v1/admin/log-debugger/agent/chat`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
            'X-Client-Platform': 'backend',
          },
          body: JSON.stringify({
            message,
            ...(state.conversationId && { conversationId: state.conversationId }),
            ...(opts?.logGroups && { logGroups: opts.logGroups }),
            ...(opts?.timeRange && { timeRange: opts.timeRange }),
          }),
          signal: controller.signal,
        });

        if (!response.ok) {
          if (response.status === 401) {
            dispatch({ type: 'SET_ERROR', error: 'Session expired. Please log in again.' });
            return;
          }
          if (response.status === 429) {
            dispatch({
              type: 'SET_ERROR',
              error: 'Rate limit exceeded. Please wait before sending another query.',
            });
            return;
          }
          let errorMsg = `Request failed with status ${response.status}`;
          try {
            const errorBody = (await response.json()) as { message?: string };
            if (errorBody.message) errorMsg = errorBody.message;
          } catch {
            // Use default error message
          }
          dispatch({ type: 'SET_ERROR', error: errorMsg });
          return;
        }

        const reader = response.body?.getReader();
        if (!reader) {
          dispatch({ type: 'SET_ERROR', error: 'Failed to read response stream' });
          return;
        }

        const decoder = new TextDecoder();
        let buffer = '';

        let receivedDone = false;
        let receivedError = false;
        const processEventBlock = (block: string): void => {
          for (const line of block.split('\n')) {
            if (!line.startsWith('data: ')) continue;
            const json = line.slice(6);
            if (!json) continue;
            try {
              const event = JSON.parse(json) as SSEEvent;
              switch (event.type) {
                case 'text_delta':
                  dispatch({ type: 'APPEND_TEXT_DELTA', content: event.content });
                  break;
                case 'tool_start':
                  dispatch({
                    type: 'SET_TOOL_START',
                    toolCall: {
                      name: event.toolName,
                      toolUseId: event.toolUseId,
                      input: event.input,
                      isActive: true,
                    },
                  });
                  break;
                case 'tool_result':
                  dispatch({
                    type: 'SET_TOOL_RESULT',
                    toolUseId: event.toolUseId,
                    summary: event.summary,
                  });
                  break;
                case 'error':
                  receivedError = true;
                  dispatch({ type: 'SET_ERROR', error: event.message });
                  break;
                case 'done':
                  receivedDone = true;
                  dispatch({
                    type: 'SET_DONE',
                    conversationId: event.metadata.conversationId,
                    tokensUsed: event.metadata.tokensUsed,
                  });
                  break;
              }
            } catch {
              // A malformed frame is reported as an incomplete stream unless a
              // later valid terminal event arrives.
            }
          }
        };

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer = (buffer + decoder.decode(value, { stream: true })).replaceAll('\r\n', '\n');
          const parts = buffer.split('\n\n');
          buffer = parts.pop() || '';
          for (const part of parts) processEventBlock(part);
        }

        buffer = (buffer + decoder.decode()).replaceAll('\r\n', '\n');
        if (buffer.trim()) processEventBlock(buffer);

        if (!receivedDone && !receivedError) {
          dispatch({
            type: 'SET_ERROR',
            error: 'The response stream closed before the analysis completed. Please try again.',
          });
        } else if (!receivedDone) {
          dispatch({ type: 'SET_STREAMING', isStreaming: false });
        }
      } catch (err: unknown) {
        if (err instanceof DOMException && err.name === 'AbortError') {
          dispatch({ type: 'SET_STREAMING', isStreaming: false });
          return;
        }
        dispatch({
          type: 'SET_ERROR',
          error: err instanceof Error ? err.message : 'An unexpected error occurred',
        });
      }
    },
    [getIdToken, state.conversationId]
  );

  const stopStreaming = useCallback(() => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
  }, []);

  const clearConversation = useCallback(() => {
    stopStreaming();
    const token = getIdToken();
    if (state.conversationId && token) {
      deleteConversation(state.conversationId, token).catch(() => {
        // Silently ignore cleanup errors
      });
    }
    dispatch({ type: 'CLEAR_CONVERSATION' });
  }, [stopStreaming, getIdToken, state.conversationId]);

  return {
    messages: state.messages,
    isStreaming: state.isStreaming,
    activeToolCall: state.activeToolCall,
    conversationId: state.conversationId,
    error: state.error,
    tokensUsed: state.tokensUsed,
    sendMessage,
    clearConversation,
    stopStreaming,
  };
}
